import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'underscore';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import {
  ADVANCED_ANALYTICS,
  IBestDataset,
  IItemsInfo,
  IPlotVariable,
} from '../../../../types/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart';
import { MarkerItem } from '../../../../types/home';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { formatRangeEnd, formatRangePartToTime } from '../../../../utils/dateRange';

interface UseExceedanceParams {
  coordinates: number[];
  previewDatasetId: string;
  datasets: IBestDataset[];
  onDatasetChange: (item: string) => void;
  dateRange: [moment.Moment, moment.Moment];
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  exceedanceValuesImage: string;
  exceedanceValuesLinks: string[];
  error: string;
  updating: boolean;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem;
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: boolean) => void;
}

export const useExceedanceParams = (params: UseExceedanceParams) => {
  const {
    coordinates,
    previewDatasetId,
    datasets,
    onDatasetChange,
    dateRange,
    enrichedMetadata,
    onUpdate,
    exceedanceValuesImage,
    pointName,
    userInput,
    updateUserInput,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  } = params;

  const selfTriggered = useRef(false);
  const defaultsAppliedForRef = useRef<string | null>(null);

  const [listVariablesX, setListVariableX] = useState<IPlotVariable[]>([]);
  const [dataset, setDataset] = useState(userInput.Exceedance.dataset);
  const [variableX, setVariableX] = useState<IPlotVariable | null>(userInput.Exceedance.variable);
  const [startDate, setStartDate] = useState(
    userInput.Exceedance.startDate || moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(userInput.Exceedance.endDate || moment(dateRange[1]));

  const getImage = useCallback(
    (indexX: IPlotVariable, orderId: string, startDate: string, endDate: string) => {
      const [longitude, latitude] = coordinates;
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.EXCEEDANCE,
          DatasetId: previewDatasetId,
          OriginalDatasetId: previewDatasetId,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, previewDatasetId, pointName]
  );

  const onUpdateExceedance = useCallback(
    (dateRange?: any) => {
      if (enrichedMetadata && variableX) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === dataset && task.CoordinatesString === coordinates.join()
        );

        if (task) {
          getImage(
            variableX,
            task.OrderId,
            formatRangePartToTime(dateRange ? dateRange[0] : startDate),
            formatRangeEnd(dateRange ? dateRange[1] : endDate)
          );
        }
      }
    },
    [enrichedMetadata, dataset, startDate, endDate, variableX, pointName]
  );

  const doVariableChange = useCallback(
    (active: number) => {
      const item = listVariablesX.find((e) => e.Index === active);
      setVariableX(item!);
    },
    [listVariablesX]
  );

  const doStartDateChange = useCallback(
    (value: any) => {
      if (value) {
        setStartDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([value, endDate]);
        }
      }
    },
    [endDate, isForAllAnalytics, setGlobalDateRange]
  );

  const doEndDateChange = useCallback(
    (value: any) => {
      if (value) {
        setEndDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([startDate, value]);
        }
      }
    },
    [startDate, isForAllAnalytics, setGlobalDateRange]
  );

  const onSelectDataset = useCallback(
    (value: string) => {
      onDatasetChange(value);
    },
    [onDatasetChange]
  );

  const handleCheckboxChange = useCallback(() => {
    setGlobalDateRange([startDate, endDate]);
    setIsForAllAnalytics(!isForAllAnalytics);
    selfTriggered.current = true;
  }, [startDate, endDate, setGlobalDateRange, isForAllAnalytics, setIsForAllAnalytics]);

  const disabledStartDate = useCallback(
    (current: Moment) => {
      const isAfterEndDate = current && current > endDate;
      return isAfterEndDate ? isAfterEndDate : current.isBefore(dateRange[0], 'day');
    },
    [endDate, dateRange]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      const isBeforeStartDate = current && current < startDate;
      return isBeforeStartDate ? isBeforeStartDate : current.isAfter(dateRange[1], 'day');
    },
    [startDate, dateRange]
  );

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => bd.label);
    return Array.from(new Set(label));
  }, [datasets]);

  const getRestoredVariable = useCallback(
    (itemsInfos: IItemsInfo[]) => {
      const exceedanceVariable = userInput.Exceedance.variable;
      if (!exceedanceVariable) {
        return;
      }
      return itemsInfos.find((item) => item.Index === exceedanceVariable?.Index);
    },
    [userInput.Exceedance.variable]
  );

  useEffect(() => {
    updateUserInput({
      Exceedance: {
        dataset: dataset,
        variable: variableX,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [dataset, variableX, startDate, endDate]);

  useEffect(() => {
    if (enrichedMetadata) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === previewDatasetId && task.CoordinatesString === coordinates.join()
      );
      if (task) {
        const itemsInfos = task.ItemsInfo;
        if (itemsInfos.length > 0) {
          setListVariableX(itemsInfos);
          const indexX = itemsInfos[0];
          const selectionKey = `${previewDatasetId}|${coordinates.join(',')}|${itemsInfos.length}`;
          if (
            previewDatasetId &&
            !isEmpty(datasets) &&
            !isEmpty(coordinates) &&
            defaultsAppliedForRef.current !== selectionKey
          ) {
            defaultsAppliedForRef.current = selectionKey;

            const restoredVariable = getRestoredVariable(itemsInfos);

            const restoredIndexX = restoredVariable ?? indexX;
            const exceedanceStartDate = userInput.Exceedance.startDate;
            const exceedanceEndDate = userInput.Exceedance.endDate;

            const hasExceedanceDateSet = !!exceedanceStartDate && !!exceedanceEndDate;

            let initialDateRange = dateRange;
            if (isForAllAnalytics) {
              initialDateRange = globalDateRange as [moment.Moment, moment.Moment];
              setStartDate(initialDateRange[0]);
              setEndDate(initialDateRange[1]);
            } else if (hasExceedanceDateSet) {
              initialDateRange = [exceedanceStartDate, exceedanceEndDate];
              setStartDate(initialDateRange[0]);
              setEndDate(initialDateRange[1]);
            } else {
              setStartDate(moment(initialDateRange[0]));
              setEndDate(moment(initialDateRange[1]));
            }
            setDataset(previewDatasetId);
            setVariableX(restoredIndexX);
            selfTriggered.current = true;
            getImage(
              restoredIndexX,
              task.OrderId,
              formatRangePartToTime(initialDateRange[0]),
              formatRangeEnd(initialDateRange[1])
            );
          }
        }
      }
    }
  }, [
    previewDatasetId,
    datasets,
    enrichedMetadata,
    coordinates,
    isForAllAnalytics,
    globalDateRange,
    getRestoredVariable,
  ]);

  useEffect(() => {
    if (isForAllAnalytics && exceedanceValuesImage !== '') {
      if (selfTriggered.current) {
        selfTriggered.current = false;
        return;
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateExceedance(globalDateRange);
      }
      setStartDate(globalDateRange[0]);
      setEndDate(globalDateRange[1]);
    }
  }, [isForAllAnalytics, exceedanceValuesImage]);

  return {
    listVariablesX,
    dataset,
    variableX,
    startDate,
    endDate,
    categories,
    onUpdateExceedance,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  };
};
